import { Button, Form, LayoutGrid, Spinner } from '@asktia/tia-ui'
import { get as _get, omit as _omit } from 'lodash'
import { FC, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { GluedDisplayQuestion } from 'src/flows/questionnaires/components/Input/Display'
import { GluedLongOpenInputQuestion } from 'src/flows/questionnaires/components/Input/LongOpenInput'
import { useQuestionnaire } from 'src/flows/questionnaires/hooks/useQuestionnaire'
import { useNavigation, useUserInfo } from 'src/hooks'
import { ErrorSavingBanner } from '../../../components/ErrorSavingBanner'
import { GluedBooleanInputQuestion } from '../../../components/Input/BooleanInput'
import { GluedMultiChoiceQuestion } from '../../../components/Input/MultiChoice'
import { GluedOpenInputQuestion } from '../../../components/Input/OpenInput'
import { GluedSingleChoiceQuestion } from '../../../components/Input/SingleChoice'
import { QuestionnaireDivider } from '../../../components/QuestionnaireDivider'
import { getFormDone } from '../../../getFormDone'
import { useQuestionnaireResponse } from '../../../hooks/useQuestionnaireResponse'
import { LegalDisclaimer } from 'src/flows/questionnaires/MentalHealth/components/LegalDisclaimer'
import { QuestionContainer } from 'src/flows/questionnaires/components/QuestionContainer'
import { ampli } from 'src/ampli'

export const OLGLP1Page: FC = () => {
    const formMethods = useForm<FieldValues>({
        defaultValues: {},
        mode: 'all'
    })

    const { user } = useUserInfo()
    const [searchParams] = useSearchParams()
    const { navigate } = useNavigation()
    const [showSavingError, setShowSavingError] = useState(false)
    const { questionnaire } = useQuestionnaire('ol-glp1')
    const { answersMap, questionnaireResponse, saveQuestionnaireResponse } =
        useQuestionnaireResponse(
            'ol-glp1',
            searchParams.get('appointmentProfileUuid') as string
        )
    const { formDone } = getFormDone(formMethods.getValues())

    const getValidFormValues = async (triggeredByFieldName?: string) => {
        await formMethods.trigger(triggeredByFieldName)

        const formValues = formMethods.getValues()
        const formKeys = Object.keys(formValues)

        const validFormValues: any = {}
        const fieldErrors = formMethods.formState.errors

        formKeys.forEach((key: string) => {
            const fieldValue = _get(formValues, key)
            const nestedFieldErrors = _get(fieldErrors, key)

            if (Array.isArray(fieldValue) && fieldValue.length === 0) {
                return
            }
            if (fieldValue === undefined) {
                return
            }

            validFormValues[key as string] =
                typeof fieldValue === 'object' && !Array.isArray(fieldValue)
                    ? _omit(fieldValue, Object.keys(nestedFieldErrors || {}))
                    : fieldValue
        })

        return validFormValues
    }

    async function triggerSave(triggeredByFieldName?: string) {
        setShowSavingError(false)
        try {
            await saveQuestionnaireResponse(
                await getValidFormValues(triggeredByFieldName)
            )
        } catch (e) {
            if (triggeredByFieldName) {
                formMethods.setValue(triggeredByFieldName, undefined)
            }
            setShowSavingError(true)
        }
    }

    const onComplete = async () => {
        triggerSave()

        ampli.mwlIntakeCompletedByPatient({ patientId: user!.id })

        navigate(
            searchParams.get('onCompleteRelativePath') ??
                '/your-care/health-record'
        )
    }

    if (!questionnaire.item) {
        return <Spinner />
    }

    const questionFormGlue = {
        questionnaire,
        questionnaireResponse,
        answersMap,
        triggerSave
    }

    const canProceed = formDone && Object.keys(formMethods.errors).length === 0

    return (
        <>
            {showSavingError && <ErrorSavingBanner />}
            <LayoutGrid columns="one" variant="condensed">
                <FormProvider {...formMethods}>
                    <Form useFormMethods={formMethods} onSubmit={() => {}}>
                        {/* 1. What are your weight management goals? */}
                        <QuestionContainer>
                            <GluedLongOpenInputQuestion
                                linkId="q-tia-goals"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 2. Are you interested in discussing medication to help reach your weight management goals? */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q-tia-medication"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 3. Let us know what you'd be interested in discussing: (multi-select) */}
                        <QuestionContainer>
                            <GluedMultiChoiceQuestion
                                linkId="q-tia-discussing"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 4. How often do you exercise? (enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q-tia-exercise-frequency"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 5. How long is each exercise session? (enableWhen: q-tia-exercise-frequency in [once, 2-3, 4-5, 6+]) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q-tia-exercise-duration"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 6. Please describe what you typically have for breakfast, lunch, and dinner. (enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedLongOpenInputQuestion
                                linkId="q-tia-diet"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 7. Do any of the following apply to you? (multi-select, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedMultiChoiceQuestion
                                linkId="q1"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 8. Do any of these health conditions or situations apply to you? (multi-select, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedMultiChoiceQuestion
                                linkId="q2"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 8.1. Do any of the following weight-related medical conditions apply to you? (multi-select, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedMultiChoiceQuestion
                                linkId="q13"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 9. Are you currently taking or have recently (within the last 12 months) taken medication(s) for weight loss? (enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q3"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 10. Are you currently gaining/re-gaining weight? (enableWhen: q3 == c or d) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q3i"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 11. Please list the name, dose, frequency, and timeline of your weight loss medication(s). (enableWhen: q3 == a, b, c, d, e) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q3-a"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 12. What was your starting weight in pounds (lbs)? (enableWhen: q3 == a, b, c, d) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q3-b"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 13. What is your current weight in pounds (lbs)? (enableWhen: q3 == a, b, c, d) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q3-c"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 14. Do you agree to only obtain weight loss medication through this platform moving forward? (enableWhen: q3 == a, b, c, d) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q3-d"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 15. When was your last dose of medication? (enableWhen: q3 == a, b) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q3-e"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 16. (acknowledge) If your last dose of GLP-1 medication was greater than 4 weeks ago... (enableWhen: q3-e == more-4-weeks) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q3-g"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 17. Please answer the following regarding your medication and allergy history: (display, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedDisplayQuestion
                                title
                                linkId="g47"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 18. I currently take prescription or over the counter medications. (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q4-a"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 19. Please include name, dose, and frequency of all your medications. (string, enableWhen: q4-a == true) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                variant="info"
                                linkId="q4-a-i"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 20. I am currently taking, plan to take, or have recently (within the last 3 months) taken opiate pain medications... (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q4-b"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 21. Please include date range, name, dose, and frequency. (string, enableWhen: q4-b == true) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                variant="info"
                                linkId="q4-b-i"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 22. I have medication allergies (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q4-c"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 23. Please provide details including name and reaction. (string, enableWhen: q4-c == true) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                variant="info"
                                linkId="q4-c-i"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        <QuestionnaireDivider />
                        {/* 24. Have you had prior bariatric (weight loss) surgery or any abdominal/pelvic surgeries? (choice, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q5"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 25. Please list all your prior bariatric (weight loss), abdominal, and pelvic surgeries. (text, enableWhen: q5 == yes) */}
                        <QuestionContainer>
                            <GluedLongOpenInputQuestion
                                linkId="q5-a"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 26. Which of the following lifestyle factors apply to you? (display, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedDisplayQuestion
                                title
                                linkId="g52"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 27. I have attempted to lose weight in a weight management program before... (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q6-a"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 28. Please provide brief details. (string, enableWhen: q6-a == true) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                variant="info"
                                linkId="q6-a-i"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 29. I am willing to reduce my caloric intake alongside medication... (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q6-b"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        {/* 30. I am willing to increase my physical activity alongside medication... (boolean, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedBooleanInputQuestion
                                linkId="q6-c"
                                {...questionFormGlue}
                            />
                        </QuestionContainer>
                        <QuestionnaireDivider />
                        {/* 31. How has your weight changed in the last 12 months? (choice, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q7"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 32. Have you been diagnosed with prediabetes or type 2 diabetes? (choice, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q8"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 33. What was your most recent fasting glucose value? (string, enableWhen: q8 == yes) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q8-a"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 34. What was your most recent hemoglobin A1c (HbA1c) value? (string, enableWhen: q8 == yes) */}
                        <QuestionContainer>
                            <GluedOpenInputQuestion
                                linkId="q8-b"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 35. What is your current or average blood pressure range? (choice, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q9"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 36. What is your current or average resting heart rate range? (choice, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedSingleChoiceQuestion
                                linkId="q10"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 37. Based on your specific medical history and lifestyle, are you concerned about: (multi-select, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedMultiChoiceQuestion
                                linkId="g63"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        {/* 38. Is there any further information which you would like the doctor to know? (text, enableWhen: q-tia-medication == yes) */}
                        <QuestionContainer>
                            <GluedLongOpenInputQuestion
                                linkId="q12"
                                {...questionFormGlue}
                            />
                            <QuestionnaireDivider />
                        </QuestionContainer>
                        <Button
                            sx={{ mt: 6, mb: 6, py: '20px' }}
                            onClick={onComplete}
                            disabled={!canProceed}
                            fullWidth
                        >
                            Complete Customized Weight Management Intake
                        </Button>
                    </Form>
                    <LegalDisclaimer />
                </FormProvider>
            </LayoutGrid>
        </>
    )
}
